import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Bookmark, Loader2, X, Trash2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import VariableDefinitionEditor from './VariableDefinitionEditor';
import { hasVariables } from './variableEngine';

export default function FUQManageDialog({ open, onOpenChange, fuq, onSaved, onDeleted }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [query, setQuery] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [variableDefs, setVariableDefs] = useState([]);

  useEffect(() => {
    if (fuq) {
      setName(fuq.name || '');
      setDescription(fuq.description || '');
      setQuery(fuq.query || '');
      setTags(fuq.tags || []);
      setConfirmDelete(false);
      try {
        const defs = fuq.variable_definitions ? JSON.parse(fuq.variable_definitions) : [];
        setVariableDefs(Array.isArray(defs) ? defs : []);
      } catch (e) {
        setVariableDefs([]);
      }
    }
  }, [fuq]);

  const queryHasVars = hasVariables(query);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const handleSave = async () => {
    if (!name.trim() || !query.trim()) return;
    setSaving(true);
    await base44.entities.FrequentlyUsedQuery.update(fuq.id, {
      name: name.trim(),
      description: description.trim() || undefined,
      query: query.trim(),
      tags: tags.length > 0 ? tags : [],
      variable_definitions: variableDefs.length > 0 ? JSON.stringify(variableDefs) : undefined
    });
    setSaving(false);
    onSaved?.();
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    await base44.entities.FrequentlyUsedQuery.delete(fuq.id);
    setDeleting(false);
    onDeleted?.();
    onOpenChange(false);
  };

  if (!fuq) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sentinel-fuqmanagedialog-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bookmark className="w-4 h-4 text-amber-400" />
            Edit FUQ
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {fuq?.id && (
            <div className="text-[10px] text-slate-500 font-mono select-all">ID: {fuq.id}</div>
          )}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Name *</label>
            <Input data-testid="sentinel-fuqmanagedialog-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <Textarea data-testid="sentinel-fuqmanagedialog-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[60px] resize-none"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">SQL Query *</label>
            <Textarea data-testid="sentinel-fuqmanagedialog-textarea-1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[80px] resize-none font-mono text-xs"
            />
          </div>

          {queryHasVars && (
            <VariableDefinitionEditor
              sqlTemplate={query}
              definitions={variableDefs}
              onChange={setVariableDefs}
            />
          )}

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tags</label>
            <div className="flex gap-2">
              <Input data-testid="sentinel-fuqmanagedialog-input-1"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag and press Enter"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              />
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(tag => (
                  <Badge key={tag} className="bg-amber-100 text-amber-800 border-amber-300 text-xs flex items-center gap-1">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-amber-950"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className={`text-xs ${confirmDelete ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'}`}
            >
              {deleting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </Button>

            <div className="flex gap-2">
              <Button data-testid="sentinel-cancel-btn" variant="outline" onClick={() => onOpenChange(false)} className="text-xs border-slate-600 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!name.trim() || !query.trim() || saving}
                className="text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}